import { parseCropType } from '../constants/CropConstants';
import { isCropHybrid } from '../components/Prescription/Dialogs/PrescriptionSettingsDialog/SeedingCalculator/SeedingCalcUtils';
import { Identifier, SeedingCalculator } from '../components/Prescription/SeedingCalculator';
import { Climate } from '../constants/Climate';

type SeedingCalculatorApi = {
  cropType: string;
  hybrid: boolean;
  organic: boolean;
  seedingTime: string;
  seedbedQuality: string;
  thousandGrainWeight: number | null;
  germination: number | null;
  unitsPerSack: number | null;
  sackWeight: number | null;
  soilType: string | null;
  result: number;
  climate: Climate;
  identifierRef: string;
  versionRef: string;
};

export const mapSeedingCalculatorInputsToApi = (
  params: SeedingCalculator
): SeedingCalculatorApi => {
  return {
    cropType: params.selectedCrop,
    hybrid: isCropHybrid(params.selectedCrop),
    organic: params.isOrganic,
    seedingTime: params.seedingDate.toUTCString(),
    seedbedQuality: params.seedbedQuality,
    thousandGrainWeight: params.tgw,
    germination: params.germinationPercentage,
    unitsPerSack: params.unitsPerSack,
    sackWeight: params.sackWeight,
    soilType: params.soilType,
    result: params.result,
    climate: params.climate,
    identifierRef: params.identifier,
    versionRef: versionToString(params.version),
  };
};

export const mapSeedingCalculatorInputsFromApi = (api: SeedingCalculatorApi): SeedingCalculator => {
  return {
    selectedCrop: parseCropType(api.cropType),
    isOrganic: api.organic,
    seedingDate: new Date(api.seedingTime),
    seedbedQuality: api.seedbedQuality,
    tgw: api.thousandGrainWeight,
    germinationPercentage: api.germination,
    unitsPerSack: api.unitsPerSack,
    sackWeight: api.sackWeight,
    soilType: api.soilType,
    result: api.result,
    climate: api.climate,
    identifier: identifierFromString(api.identifierRef),
    version: versionFromString(api.versionRef),
  };
};

const versionToString = (version: number): string => {
  return `v${version}`;
};

const versionFromString = (version: string): number => {
  const trimmedVersion = version.trim();
  const match = /^v(\d+)$/i.exec(trimmedVersion);

  if (match) {
    return Number(match[1]);
  }

  const parsedVersion = Number(trimmedVersion);

  if (!Number.isFinite(parsedVersion)) {
    throw new Error(`Invalid seeding calculator version: ${version}`);
  }

  return parsedVersion;
};

const identifierFromString = (identifier: string): Identifier => {
  switch (identifier) {
    case Identifier.DANISH_SEEDING_MODEL:
      return Identifier.DANISH_SEEDING_MODEL;
    default:
      throw new Error(`Unknown seeding calculator identifier: ${identifier}`);
  }
};
